import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useStore from "../store/useStore";
import toast from "react-hot-toast";
import { FaSpinner } from "react-icons/fa";
import OrderConfirmed from "./Checkout/OrderConfirmed";

/**
 * Step 11 — Cashfree Return URL handler
 * Route: /payment/return?order_id=102
 * Cashfree redirects here after the payment flow (success or failure).
 * We fetch the order detail to get the real payment status and show the result.
 */
const CashfreeReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getOrderDetails, verifyPayment, clearCart, resetPayment, buyNowItem, clearBuyNowItem } = useStore();

  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) {
      navigate("/account/orders");
      return;
    }

    const verify = async () => {
      try {
        let data;
        try {
          data = await verifyPayment(orderId);
        } catch {
          const fallbackOrder = await getOrderDetails(orderId);
          data = {
            order: fallbackOrder,
            payment_status: fallbackOrder?.payment_status,
          };
        }

        const paymentStatus = (data?.payment_status ?? data?.order?.payment_status ?? "").toUpperCase();

        // ── Always fetch full order details (items + pricing) for the confirmation screen ──
        // The verify-payment endpoint only returns payment status, not order items/prices.
        const fullOrder = await getOrderDetails(orderId);
        const baseOrder = data?.order ?? data;
        const mergedOrder = fullOrder
          ? { ...baseOrder, ...fullOrder, payment_status: paymentStatus || baseOrder?.payment_status }
          : baseOrder;
        setOrder(mergedOrder);

        if (paymentStatus === "PAID" || paymentStatus === "SUCCESS") {
          setStatus("success");
          toast.success("Payment completed successfully!");
          if (buyNowItem) clearBuyNowItem();
          else await clearCart();
          resetPayment();
        } else {
          setStatus("failed");
          toast.error("Payment was not completed. You can retry from My Orders.");
        }
      } catch (err) {
        setStatus("failed");
        toast.error(err.message || "Payment verification failed. Check My Orders.");
      }
    };

    verify();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === "checking") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50">
        <FaSpinner className="h-10 w-10 animate-spin text-primary-600" />
        <p className="text-sm font-semibold text-gray-500">Verifying your payment…</p>
      </div>
    );
  }

  if (status === "success") {
    return <OrderConfirmed order={order} />;
  }

  // failed
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
          <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="mb-2 text-2xl font-extrabold text-gray-900">Payment Failed</h1>
        <p className="mb-2 text-gray-500">
          Order <span className="font-bold text-gray-800">{order?.order_number ?? `#${orderId}`}</span> — payment was not completed.
        </p>
        <p className="mb-8 text-sm text-gray-400">
          Your order is still reserved. You can retry payment from My Orders.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={() => navigate("/account/orders")}
            className="flex-1 rounded-2xl bg-primary-600 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-primary-700"
          >
            Retry Payment
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex-1 rounded-2xl border-2 border-gray-200 py-3.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashfreeReturn;
